import React, { useEffect, useMemo, useState } from "react";
import { Search, FileSpreadsheet, Download } from "lucide-react";
import api, { formatErr } from "../lib/api";

/**
 * DatasheetsTab — equipment / pipe-class / electrical-load / civil-foundation
 * datasheets rendered as structured tables.
 *
 * It pulls deliverables that carry `precomputed` numerics (V2 specialists ship
 * pre-computed engineering data for vessels, HX, columns, foundations, pipe
 * classes, motors, etc.) and renders them as flat key→value tables, recursing
 * into lists/dicts.
 *
 * Also supports `content_table` for handcrafted tables.
 */
export default function DatasheetsTab({ projectId }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/deliverables?has_precomputed=true`);
        if (!alive) return;
        setRows(data || []);
        if (data && data.length) setOpenId(data[0].id);
      } catch (e) {
        if (alive) setErr(formatErr(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (!filter.trim()) return rows;
    const q = filter.toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.code || "").toLowerCase().includes(q) ||
      (r.stage_code || "").toLowerCase().includes(q) ||
      JSON.stringify(r.precomputed || {}).toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const open = filtered.find((r) => r.id === openId) || filtered[0];

  const downloadJson = (d) => {
    const blob = new Blob([JSON.stringify(d.precomputed || {}, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${d.code.replace(/\W+/g, "_")}_datasheet.json`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const downloadCsv = (d) => {
    const csv = flattenForCsv(d.precomputed || {});
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${d.code.replace(/\W+/g, "_")}_datasheet.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  if (err) return <div className="pf-sev pf-sev-error" style={{ display: "block", padding: 12 }}>{err}</div>;
  if (rows === null) return <div style={{ padding: 24, color: "var(--pf-text-muted)" }}>Loading datasheets…</div>;
  if (rows.length === 0) {
    return (
      <div data-testid="datasheets-empty" style={{ padding: 32, textAlign: "center", color: "var(--pf-text-muted)" }}>
        <FileSpreadsheet size={32} style={{ opacity: 0.4 }} />
        <div style={{ marginTop: 12, fontSize: 14 }}>No datasheets yet.</div>
        <div style={{ marginTop: 6, fontSize: 12 }}>Run discipline stages (e.g. Process Plant C / D / E / F / G) to generate datasheets with deterministic numerics.</div>
      </div>
    );
  }

  return (
    <div data-testid="datasheets-tab" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 0, height: "100%" }}>
      <div style={{ borderRight: "1px solid var(--pf-border)", overflow: "auto", background: "var(--pf-surface)" }}>
        <div style={{ padding: 12, borderBottom: "1px solid var(--pf-border)" }}>
          <div className="pf-overline" style={{ marginBottom: 6 }}>DATASHEETS · {rows.length}</div>
          <div style={{ position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 8, top: 9, color: "var(--pf-text-dim)" }} />
            <input
              type="text"
              className="pf-input"
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="datasheets-filter"
              style={{ fontSize: 12, padding: "6px 10px 6px 26px" }}
            />
          </div>
        </div>
        <div style={{ padding: 8, display: "grid", gap: 6 }}>
          {filtered.map((d) => (
            <button
              key={d.id}
              type="button"
              className="pf-card"
              onClick={() => setOpenId(d.id)}
              data-testid={`datasheet-row-${d.code}`}
              style={{
                padding: 10, textAlign: "left", cursor: "pointer",
                borderColor: open?.id === d.id ? "var(--pf-spark)" : "var(--pf-border)",
                background: open?.id === d.id ? "var(--pf-surface-2)" : "var(--pf-surface)",
              }}
            >
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--pf-spark)" }}>
                {d.stage_code} · {d.code} · v{d.version}
              </div>
              <div className="pf-heading" style={{ fontSize: 13, margin: "3px 0 0" }}>{d.name}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ overflow: "auto", padding: 24 }}>
        {open && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div className="pf-overline">{open.stage_code} · {open.code} · v{open.version}</div>
                <div className="pf-heading" style={{ fontSize: 18, margin: "4px 0 0" }}>{open.name}</div>
                {open.summary && <div style={{ fontSize: 12, color: "var(--pf-text-muted)", marginTop: 4 }}>{open.summary}</div>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-ghost" onClick={() => downloadCsv(open)} data-testid="datasheet-download-csv"><Download size={13} /> .csv</button>
                <button className="btn btn-primary" onClick={() => downloadJson(open)} data-testid="datasheet-download-json"><Download size={13} /> .json</button>
              </div>
            </div>
            <DatasheetTables data={open.precomputed || {}} />
          </>
        )}
      </div>
    </div>
  );
}

function DatasheetTables({ data }) {
  if (!data || typeof data !== "object") return null;
  const sections = Object.entries(data);
  return (
    <div style={{ display: "grid", gap: 18 }}>
      {sections.map(([key, val]) => (
        <DatasheetSection key={key} name={key} value={val} />
      ))}
    </div>
  );
}

function DatasheetSection({ name, value }) {
  if (Array.isArray(value)) {
    return (
      <div className="pf-card" style={{ padding: 14 }}>
        <div className="pf-overline" style={{ marginBottom: 8 }}>{prettyKey(name)} · {value.length} items</div>
        {value.length > 0 && typeof value[0] === "object" && value[0] !== null
          ? <RowTable rows={value} />
          : <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{value.map((v, i) => <li key={i}>{String(v)}</li>)}</ul>}
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <div className="pf-card" style={{ padding: 14 }}>
        <div className="pf-overline" style={{ marginBottom: 8 }}>{prettyKey(name)}</div>
        <KeyValueTable obj={value} />
      </div>
    );
  }
  return (
    <div className="pf-card" style={{ padding: 12 }}>
      <strong style={{ fontSize: 13 }}>{prettyKey(name)}:</strong> <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{String(value)}</span>
    </div>
  );
}

function KeyValueTable({ obj }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <tbody>
        {Object.entries(obj).map(([k, v]) => (
          <tr key={k} style={{ borderBottom: "1px solid var(--pf-border)" }}>
            <td style={{ padding: "6px 12px 6px 0", color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, width: "40%", verticalAlign: "top" }}>{prettyKey(k)}</td>
            <td style={{ padding: "6px 0", color: "var(--pf-text)", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, wordBreak: "break-word" }}>
              {v && typeof v === "object" ? <pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(v, null, 2)}</pre> : String(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RowTable({ rows }) {
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r || {}).forEach((k) => s.add(k)); return s; }, new Set()));
  return (
    <div style={{ overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid var(--pf-border)" }}>
            {cols.map((c) => (
              <th key={c} style={{ padding: "6px 8px", textAlign: "left", color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600 }}>{prettyKey(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {cols.map((c) => (
                <td key={c} style={{ padding: "5px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, color: "var(--pf-text)" }}>
                  {(() => {
                    const v = r?.[c];
                    if (v === null || v === undefined) return "—";
                    if (typeof v === "object") return <code style={{ fontSize: 10.5 }}>{JSON.stringify(v)}</code>;
                    return String(v);
                  })()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function prettyKey(k) {
  return String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function flattenForCsv(obj, prefix = "") {
  const lines = ["key,value"];
  const walk = (o, p) => {
    if (o === null || o === undefined) { lines.push(`"${p}",`); return; }
    if (typeof o !== "object") { lines.push(`"${p}","${String(o).replace(/"/g, '""')}"`); return; }
    if (Array.isArray(o)) {
      o.forEach((v, i) => walk(v, p ? `${p}[${i}]` : `[${i}]`));
      return;
    }
    Object.entries(o).forEach(([k, v]) => walk(v, p ? `${p}.${k}` : k));
  };
  walk(obj, prefix);
  return lines.join("\n");
}
